import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowRight,
  Award,
  BadgeCheck,
  ClipboardList,
  FileCheck,
  Globe,
  Scale,
  ShieldCheck,
  Stamp,
} from 'lucide-react';

const auditProcess = [
  {
    title: 'Application',
    description:
      'QSC America begins with an application and contract review to confirm the requested standard, certification scope, sites, activities, complexity, and required audit time. We verify that we have the competence, impartiality, and resources needed before accepting the engagement.',
    bullets: [
      'Review organization details, legal entity, scope, sites, and outsourced processes.',
      'Confirm applicable standard, audit program, technical competence, and audit duration.',
      'Issue a proposal and certification agreement once feasibility and impartiality checks are complete.',
    ],
  },
  {
    title: 'Stage 1',
    description:
      'Stage 1 assesses readiness for certification. The audit team reviews documented information, site-specific conditions, key management system processes, internal audits, management review, and preparedness for Stage 2.',
    bullets: [
      'Confirm scope, statutory and regulatory context, and management system maturity.',
      'Evaluate location-specific conditions and identify areas of concern for Stage 2.',
      'Agree the Stage 2 audit plan and any readiness actions required before proceeding.',
    ],
  },
  {
    title: 'Stage 2',
    description:
      'Stage 2 evaluates the implementation and effectiveness of the management system. QSC America audits processes, records, objectives, risk controls, and operational practices across the certified scope and sampled locations as applicable.',
    bullets: [
      'Verify implementation against the standard and the organization’s own planned arrangements.',
      'Interview personnel, review records, and observe activities on site or through approved remote methods.',
      'Record nonconformities, observations, and positive practices, then hold a closing meeting.',
    ],
  },
  {
    title: 'Surveillance',
    description:
      'Surveillance audits are conducted at least annually to confirm the management system remains effective and certification conditions continue to be met throughout the cycle.',
    bullets: [
      'Review progress on previous findings, complaints, changes, objectives, and continual improvement.',
      'Sample core management system processes and selected operational controls.',
      'Confirm the scope, sites, and activities remain accurately represented.',
    ],
  },
  {
    title: 'Recertification',
    description:
      'Before the certificate expires, QSC America performs a recertification audit to confirm continuing conformity and effectiveness across the full certification cycle and to support renewal of certification.',
    bullets: [
      'Review overall system performance over the full cycle.',
      'Assess effectiveness of corrective action, internal audit, management review, and continual improvement.',
      'Recommend renewal only after successful completion and independent review.',
    ],
  },
  {
    title: 'Special Audits',
    description:
      'Special audits may be required to investigate complaints, significant changes, transfers, scope changes, short-notice matters, or follow up on suspended or high-risk situations.',
    bullets: [
      'May be announced or short notice depending on risk, complaint, or certification status.',
      'Focused on the triggering issue, affected scope, site, or process.',
      'Results may affect certification status, scope, or surveillance frequency.',
    ],
  },
  {
    title: 'Certification Decision',
    description:
      'Certification decisions are made independently of the audit team. QSC America reviews the audit record, closure of nonconformities where required, and contractual conditions before granting, renewing, changing, suspending, or withdrawing certification.',
    bullets: [
      'Decision-makers are independent of the audit activities being reviewed.',
      'Certification is granted only when objective evidence supports conformity.',
      'Conditions, limitations, or scope updates are formally documented on the certificate record.',
    ],
  },
  {
    title: 'Handling Nonconformities',
    description:
      'QSC America requires clients to analyze cause, propose correction and corrective action, and submit evidence within the defined response period. Major nonconformities must be effectively addressed before certification can be granted or renewed.',
    bullets: [
      'Minor nonconformities require corrective action and are verified during follow-up activity or the next audit, as appropriate.',
      'Major nonconformities require timely correction, root-cause action, and verification before certification decisions can proceed.',
      'Failure to adequately close findings may lead to refusal, suspension, scope reduction, or withdrawal.',
    ],
  },
];

const certificationLifecycle = [
  {
    title: 'Grant Certification',
    text: 'QSC America grants certification after successful completion of the audit program, satisfactory resolution of required nonconformities, and an independent certification decision.',
  },
  {
    title: 'Refuse Certification',
    text: 'Certification is refused when audit evidence does not support conformity, major nonconformities are not effectively resolved, or contractual and program requirements are not met.',
  },
  {
    title: 'Maintain Certification',
    text: 'Certification is maintained through successful surveillance activity, timely corrective action, continued compliance with the certification agreement, and accurate representation of certified scope.',
  },
  {
    title: 'Renew Certification',
    text: 'Certification is renewed after successful recertification before expiry and completion of an independent decision confirming ongoing conformity and effectiveness.',
  },
  {
    title: 'Suspend Certification',
    text: 'Certification may be suspended for serious breakdowns in the management system, failure to allow required audits, misuse of marks, unresolved major findings, or non-payment under contractual terms.',
  },
  {
    title: 'Restore Certification',
    text: 'Suspended certification may be restored when the client demonstrates that the cause of suspension has been fully addressed within the allowed timeframe and QSC America verifies effectiveness.',
  },
  {
    title: 'Withdraw Certification',
    text: 'Certification is withdrawn when suspension is not resolved in time, the client voluntarily cancels, the management system no longer conforms, or certified claims can no longer be supported.',
  },
  {
    title: 'Expand or Reduce Scope',
    text: 'Scope changes are controlled through application review and additional audit activity where required. Scope is reduced when parts of the certified activities, sites, or services no longer meet requirements.',
  },
];

const standards = [
  {
    code: 'ISO 9001:2015',
    title: 'Quality Management Systems',
    description: 'Certification for organizations seeking structured quality management, customer focus, and continual improvement.',
  },
  {
    code: 'ISO/IEC 20000-1:2018',
    title: 'IT Service Management Systems',
    description: 'Certification for organizations that design, manage, and improve IT-enabled services.',
  },
  {
    code: 'ISO/IEC 27001:2022',
    title: 'Information Security Management Systems',
    description: 'Certification for organizations managing confidentiality, integrity, and availability of information assets.',
  },
  {
    code: 'ISO/IEC 42001:2023',
    title: 'Artificial Intelligence Management Systems',
    description: 'Certification for organizations governing the responsible development and use of AI systems.',
  },
];

const clientLogoRequirements = [
  'When referring to its certification status in any form of communication — including the internet, brochures, advertising materials, or any other documents — the client shall strictly adhere to the conditions specified in the Certification Agreement and the QSC America Logo and Mark Usage Guidelines.',
  'Any certification claims that could be misleading or misunderstood by the public are not permitted.',
  'The client shall not use the full certification documents, or any portions thereof, in a manner that may cause misunderstanding.',
  'In the event of suspension or withdrawal of certification, the client shall immediately cease use of all advertising and promotional materials containing the QSC America logo, in accordance with the directions provided by QSC America.',
  'In the event of a reduction in the scope of certification, the client shall promptly update or withdraw all advertising and promotional materials to reflect the revised certified scope.',
  'The client shall not imply that the certification applies to specific products or processes of the management system, and the certification shall not be used in connection with activities outside the certified scope.',
  'The certification logo shall not be used in any way that could undermine public confidence or damage the reputation and credibility of QSC America or the certification system.',
  'The accreditation mark shall not be used in isolation. It may be used only in conjunction with the QSC America certification logo, within the recognized scope of accreditation and during its validity period. If the accreditation scope is cancelled or reduced, use of the accreditation mark must cease immediately for the affected scope.',
  'The QSC America certification logo may be used on promotional materials related to the certified field, but shall not be applied to product packaging or unit labels.',
];

const permittedLogoUses = [
  'Printed materials or manufactured promotional items.',
  'Nameplates, business cards, textbooks, stationery, brochures, documents, and invoices.',
  'Certificates and other materials issued by the certification body that are not classified as the client’s product.',
];

const restrictedLogoUses = [
  'Statements related to transaction samples, or any other assertions of product conformity.',
  'Flags, buildings, or vehicles.',
  'Laboratory test, calibration, or inspection reports, as these documents are classified as products under this policy.',
  'Product packaging, unit labels, or certificates of analysis.',
  'Any use after suspension, withdrawal, or expiry of certification, or for sites and activities outside the certified scope.',
];

const complaintsSteps = [
  'Complaints and appeals may be submitted by clients, certified organizations, interested parties, or members of the public by email or in writing to QSC America.',
  'QSC America acknowledges receipt and records the matter, then performs an initial review to confirm whether it relates to certification activities under our control.',
  'Complaints are investigated by personnel who were not involved in the subject matter where independence is required. Appeals are reviewed by competent persons independent of the original decision.',
  'QSC America communicates the outcome or status update once review milestones are reached and closes the case after actions are completed and verified, where applicable.',
];

const appealTargets = [
  'Acknowledgement target: within 5 business days.',
  'Initial review target: within 10 business days of receiving sufficient information.',
  'Formal response target: typically within 30 business days, subject to case complexity and external evidence needs.',
  'Where additional time is required, QSC America provides a status update and revised response target.',
];

const PublicCertificationInfoPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-gradient-to-r from-[#003f6d] via-[#004a85] to-[#003f6d] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">Public Certification Information</h1>
          <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
          <p className="text-xl text-gray-200 max-w-4xl">
            QSC America shares the following information on how our certification process works, how
            certification decisions are made, how complaints and appeals are handled, and how impartiality
            is protected across our management systems certification activities.
          </p>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <ClipboardList className="w-10 h-10 text-[#003f6d] mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Transparent Audit Program</h2>
                <p className="text-gray-600">
                  Public description of application, Stage 1, Stage 2, surveillance, recertification,
                  special audits, certification decision, and nonconformity handling.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Award className="w-10 h-10 text-[#c1272d] mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Certification Lifecycle</h2>
                <p className="text-gray-600">
                  Clear explanation of how certification is granted, refused, maintained, renewed,
                  suspended, restored, withdrawn, or changed in scope.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <ShieldCheck className="w-10 h-10 text-[#d4af37] mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Client Confidence</h2>
                <p className="text-gray-600">
                  Published rules for marks, complaints, appeals, impartiality, and information available
                  on request.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Policy on Impartiality</h2>
              <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                QSC America is committed to safeguarding impartiality in all certification activities. We
                identify, analyze, document, and manage risks to impartiality arising from ownership,
                finances, commercial pressures, personal relationships, prior involvement, and other
                conflicts of interest.
              </p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" /><span>Certification decisions are separated from audit execution.</span></li>
                <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" /><span>Certification activities are governed to protect independence, objectivity, and impartial decisions.</span></li>
              </ul>
            </div>

            <Card className="border-t-4 border-t-[#003f6d] h-fit">
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Impartiality Committee</h3>
                <p className="text-gray-600 mb-4">
                  QSC America maintains an independent Impartiality Committee that provides oversight of all
                  certification activities and safeguards against conflicts of interest, commercial pressures,
                  or undue influence on certification decisions.
                </p>
                <p className="text-gray-600 text-sm">
                  To raise an impartiality concern or request information about the committee, contact us at{' '}
                  <a href="mailto:info@qscamerica.com?subject=Impartiality%20Committee%20Enquiry" className="text-[#003f6d] font-medium hover:underline">
                    info@qscamerica.com
                  </a>{' '}
                  with subject line: <span className="font-medium">Impartiality Committee Enquiry</span>.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Audit Process Description</h2>
            <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
            <p className="text-xl text-gray-600 max-w-4xl">
              QSC America plans and conducts audits using a risk-based and competence-based approach so
              that certification decisions are supported by objective evidence.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {auditProcess.map((item) => (
              <Card key={item.title} className="h-full border-t-4 border-t-[#003f6d]">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">{item.description}</p>
                  <ul className="space-y-2">
                    {item.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-gray-600">
                        <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Certification Process Description</h2>
            <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
            <p className="text-xl text-gray-600 max-w-4xl">
              Certification status changes are controlled decisions. QSC America documents the basis for
              every decision and communicates conditions that affect certified scope and public claims.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {certificationLifecycle.map((item, index) => (
              <Card key={item.title} className={index % 2 === 0 ? 'border-l-4 border-l-[#003f6d]' : 'border-l-4 border-l-[#c1272d]'}>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Management Systems & Schemes Offered</h2>
            <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
            <p className="text-xl text-gray-600 max-w-4xl">
              QSC America’s public certification portfolio currently includes the management system
              standards listed below. Additional schemes are published when formally available within our
              approved competence and contract review framework.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {standards.map((standard) => (
              <Card key={standard.code} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <BadgeCheck className="w-8 h-8 text-[#003f6d] mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{standard.code}</h3>
                  <p className="font-medium text-gray-800 mb-2">{standard.title}</p>
                  <p className="text-gray-600">{standard.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Certificate of Registration — Content &amp; Format</h2>
            <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
            <p className="text-xl text-gray-600 max-w-4xl">
              Every QSC America Certificate of Registration contains the following information. Certificates
              are valid for a three-year cycle, subject to satisfactory annual surveillance audits.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { label: 'Certificate Number', detail: 'A unique reference number assigned by QSC America to identify the certification.' },
              { label: 'Certified Organization', detail: 'Full legal name of the certified organization as registered.' },
              { label: 'Registered Address', detail: 'Registered address of the certified legal entity and all certified sites.' },
              { label: 'Certification Scope', detail: 'Description of the activities, products, services, or processes covered by the certification.' },
              { label: 'Normative Document', detail: 'The applicable standard and version (e.g. ISO 9001:2015, ISO/IEC 27001:2022).' },
              { label: 'Initial Certification Date', detail: 'The date on which certification was first granted to the client.' },
              { label: 'Issue Date & Expiry Date', detail: 'The date the current certificate was issued and the date it expires (3-year validity cycle).' },
              { label: 'Audit Cycle', detail: 'Annual surveillance audits are required to maintain certification. Recertification is conducted before expiry.' },
              { label: 'Authorized Signatures', detail: 'Certificate is signed by authorized QSC America personnel. Digital and physical formats are both valid.' },
              { label: 'CB Identity', detail: 'QSC America name, logo, and contact details are displayed on every certificate.' },
              { label: 'Accreditation Reference', detail: 'Once IAS accreditation is granted, the IAS accreditation mark, accreditation number, and scope will be included on all certificates.' },
            ].map((item) => (
              <Card key={item.label} className="hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-5">
                  <BadgeCheck className="w-6 h-6 text-[#003f6d] mb-3" />
                  <h4 className="font-bold text-gray-900 mb-1">{item.label}</h4>
                  <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: item.detail }} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Rules for Use of Logo &amp; Accreditation Mark</h2>
              <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
              <p className="text-xl text-gray-600 mb-6">
                QSC America protects the integrity of certification claims by controlling how our name,
                certification logo, and any related accreditation mark may be used.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Client Requirements for Use of the QSC America Logo
              </h3>
              <p className="text-gray-600 mb-4">
                The certified client shall comply with the following requirements concerning use of the
                QSC America certification logo:
              </p>
              <ul className="space-y-3">
                {clientLogoRequirements.map((rule) => (
                  <li key={rule} className="flex items-start gap-3 text-gray-700">
                    <Stamp className="w-5 h-5 text-[#003f6d] flex-shrink-0 mt-1" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="font-semibold text-amber-900">IAS Accreditation — Assessment in Progress</p>
                <p className="mt-2 text-amber-800">
                  QSC America is currently undergoing accreditation assessment with the
                  International Accreditation Service (IAS). The IAS accreditation mark may be used in
                  conjunction with the QSC America certification logo only after accreditation is formally
                  granted, and only in accordance with the applicable IAS requirements and rules governing
                  the use of accreditation symbols and marks. Until such accreditation is granted, the IAS
                  accreditation mark shall not be used or represented in any manner that implies
                  accreditation status. Clients and interested parties will be notified when accreditation
                  is confirmed.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <Card className="border-t-4 border-t-[#003f6d] h-fit">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Permitted Use of the Logo</h3>
                  <p className="text-gray-600 mb-4">
                    The certification logo may be used in the following advertising and promotional materials:
                  </p>
                  <ul className="space-y-3 text-gray-700">
                    {permittedLogoUses.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <BadgeCheck className="w-5 h-5 text-[#003f6d] flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-[#c1272d] h-fit">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Restricted Use of the Logo and Mark</h3>
                  <p className="text-gray-600 mb-4">
                    Upon issuance of an ISO certificate, only the authorized certification logo may be
                    displayed, and no modifications to the logo are permitted. The certification logo and
                    accreditation mark shall not be used in the following cases:
                  </p>
                  <ul className="space-y-3 text-gray-700">
                    {restrictedLogoUses.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Scale className="w-5 h-5 text-[#c1272d] flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 rounded-lg bg-[#c1272d]/5 border border-[#c1272d]/15 p-4">
                    <p className="text-gray-700">
                      In the event of incorrect logo usage, the certified client is required to implement
                      corrective action without delay.
                    </p>
                    <p className="mt-2 text-gray-700">
                      <span className="font-semibold">Note:</span> Misuse of an ISO logo by a certified
                      organization may result in revocation of the ISO certificate.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Complaints & Appeals</h2>
            <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
            <p className="text-xl text-gray-600 max-w-4xl">
              QSC America accepts complaints and appeals from clients, certified organizations, and other
              interested parties. We treat each matter seriously, confidentially, and without retaliation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <FileCheck className="w-8 h-8 text-[#003f6d] mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">How to Submit</h3>
                <ul className="space-y-3 text-gray-600 mb-6">
                  {complaintsSteps.map((step) => (
                    <li key={step} className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
                <div className="rounded-lg bg-[#003f6d]/5 p-4 border border-[#003f6d]/10">
                  <p className="text-gray-700 font-medium">Submission channels</p>
                  <p className="text-gray-600 mt-2">Phone: (251) 654 7929</p>
                  <p className="text-gray-600 mt-1">
                    Email:{' '}
                    <a
                      href="mailto:info@qscamerica.com?subject=Complaints%20%26%20Appeals%20Submission"
                      className="text-[#003f6d] font-medium hover:underline"
                    >
                      info@qscamerica.com
                    </a>{' '}
                    — use subject line: <span className="font-medium">Complaints &amp; Appeals Submission</span>
                  </p>
                  <p className="text-gray-600 mt-1">Written correspondence and online submissions may also be directed through the <a href="/contact" className="text-[#003f6d] font-medium hover:underline">Contact page</a> on this website.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <Scale className="w-8 h-8 text-[#c1272d] mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Process Targets</h3>
                <ul className="space-y-3 text-gray-600 mb-6">
                  {appealTargets.map((target) => (
                    <li key={target} className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-[#c1272d] flex-shrink-0 mt-1" />
                      <span>{target}</span>
                    </li>
                  ))}
                </ul>
                <Accordion type="single" collapsible>
                  <AccordionItem value="appeal-steps">
                    <AccordionTrigger className="text-left font-semibold text-gray-900">
                      Appeals handling
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 space-y-3">
                      <p>Appeals are reviewed by competent persons independent of the original decision being challenged.</p>
                      <p>QSC America evaluates submitted evidence, may request additional information, and communicates the outcome in writing.</p>
                      <p>The appeal process does not delay actions necessary to protect the integrity of certification or the public interest.</p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#003f6d]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Quality Policy</h2>
            <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
            <p className="text-xl text-gray-600 max-w-4xl">
              QSC America's Quality Policy is publicly available and applies to all personnel, employees, and contractors.
            </p>
          </div>
          <Card className="border-l-4 border-l-[#003f6d]">
            <CardContent className="p-8">
              <blockquote className="text-lg text-gray-800 font-medium italic mb-6 border-l-4 border-[#d4af37] pl-4">
                "QSC America is committed to operating as an independent and impartial certification body,
                ensuring that all certification activities are conducted with integrity, objectivity, and
                professionalism. The organization applies robust, consistent, and evidence-based processes
                to support reliable and transparent certification decision-making."
              </blockquote>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Impartiality &amp; Independence</h4>
                  <p className="text-sm leading-relaxed">All certification decisions are based on objective audit evidence. QSC America operates without commercial, financial, or organizational influence and manages all identified risks to impartiality, including conflicts of interest.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Competence</h4>
                  <p className="text-sm leading-relaxed">QSC America selects, trains, and monitors auditors and certification decision-makers using documented competence criteria for each certification scheme. Audits are conducted only by personnel who meet defined competence requirements.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Consistency &amp; Reliability</h4>
                  <p className="text-sm leading-relaxed">Certification processes are applied according to documented procedures. Audit criteria and decision-making methods are implemented uniformly across all clients, sectors, and locations.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Continual Improvement</h4>
                  <p className="text-sm leading-relaxed">QSC America systematically uses the results of internal audits, management reviews, complaints and appeals, accreditation body feedback, and client input to identify and implement improvement opportunities.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Information Available Upon Request</h2>
            <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
            <p className="text-xl text-gray-600 max-w-4xl">
              QSC America can provide additional certification information to interested parties when
              requested and when disclosure is permitted by contract, scheme rules, and confidentiality
              obligations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <Globe className="w-8 h-8 text-[#003f6d] mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Geographical Areas of Operation</h3>
                <p className="text-gray-600 mb-3">
                  QSC America provides certification services within the <strong>United States</strong> and
                  internationally. Services are available globally, excluding countries identified as being
                  of particular concern under applicable U.S. regulatory requirements.
                </p>
                <p className="text-gray-600">
                  For confirmation of service availability in a specific country or region — including
                  language, scheme requirements, and auditor competence — contact us at{' '}
                  <a href="mailto:info@qscamerica.com" className="text-[#003f6d] font-medium hover:underline">
                    info@qscamerica.com
                  </a>.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <ClipboardList className="w-8 h-8 text-[#c1272d] mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Certification Status of a Client</h3>
                <p className="text-gray-600 mb-3">
                  On request, QSC America can provide the status of a given certification, including:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>Name of the certified client</li>
                  <li>Applicable normative document</li>
                  <li>Certified scope</li>
                  <li>City and country of the certified site or legal entity</li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-6">
            <h3 className="text-xl font-bold text-amber-900 mb-2">Accreditation Status</h3>
            <p className="text-amber-800">
              QSC America is currently undergoing assessment for accreditation by the{' '}
              <strong>International Accreditation Service (IAS)</strong>. Upon successful completion,
              QSC America's accreditation number, scope, and the IAS accreditation mark will be
              published here and displayed on all certificates and official communications. In the
              interim, certifications are issued under QSC America's authority as an independent
              certification body.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#003f6d] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Need Clarification on Certification Requirements?</h2>
          <p className="text-xl text-gray-200 mb-8">
            QSC America can walk you through scope, certification conditions, complaint handling, and the
            audit process for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#d4af37] hover:bg-[#c49b2a] text-[#003f6d] font-semibold px-8 py-6 text-lg">
                Contact QSC America
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/certification/process">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#003f6d] px-8 py-6 text-lg">
                View Certification Journey
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicCertificationInfoPage;